import { request } from './core'

export interface ArenaPersistenceOptions {
  sessionId?: string
  metaSessionId?: string
  turnId?: string
}

export interface ArenaSTTResponse {
  text: string
  latency_ms: number
}

export interface ArenaTTSResponse {
  audio_base64: string
  content_type: string
  latency_ms: number
}

export interface ArenaRerankResponse {
  results: Array<{
    content: string
    rerank_score: number
    rank: number
  }>
  latency_ms: number
}

export interface ArenaImageResponse {
  image_base64?: string | null
  mime_type?: string | null
  text?: string | null
  latency_ms: number
}

export interface ArenaVideoResponse {
  video_base64?: string | null
  mime_type?: string | null
  text?: string | null
  latency_ms: number
}

export const arenaApi = {
  stt: async (
    modelId: string,
    audioFile: File | Blob,
    options?: ArenaPersistenceOptions
  ): Promise<ArenaSTTResponse> => {
    const formData = new FormData()
    formData.append('model_id', modelId)
    formData.append('file', audioFile, (audioFile as File).name || 'audio.webm')
    if (options?.sessionId) formData.append('session_id', options.sessionId)
    if (options?.metaSessionId) formData.append('meta_session_id', options.metaSessionId)
    if (options?.turnId) formData.append('turn_id', options.turnId)

    return request<ArenaSTTResponse>('/chat/arena/stt', {
      method: 'POST',
      body: formData,
      headers: {},
    })
  },

  tts: async (
    modelId: string,
    text: string,
    voice?: string,
    options?: ArenaPersistenceOptions
  ): Promise<ArenaTTSResponse> => {
    return request<ArenaTTSResponse>('/chat/arena/tts', {
      method: 'POST',
      body: JSON.stringify({
        model_id: modelId,
        text,
        ...(voice && { voice }),
        ...(options?.sessionId && { session_id: options.sessionId }),
        ...(options?.metaSessionId && { meta_session_id: options.metaSessionId }),
        ...(options?.turnId && { turn_id: options.turnId }),
      }),
    })
  },

  rerank: async (
    modelId: string,
    query: string,
    documents: string[],
    options?: ArenaPersistenceOptions
  ): Promise<ArenaRerankResponse> => {
    return request<ArenaRerankResponse>('/chat/arena/rerank', {
      method: 'POST',
      body: JSON.stringify({
        model_id: modelId,
        query,
        documents,
        ...(options?.sessionId && { session_id: options.sessionId }),
        ...(options?.metaSessionId && { meta_session_id: options.metaSessionId }),
        ...(options?.turnId && { turn_id: options.turnId }),
      }),
    })
  },

  image: async (
    modelId: string,
    prompt: string,
    options?: ArenaPersistenceOptions
  ): Promise<ArenaImageResponse> => {
    return request<ArenaImageResponse>('/chat/arena/image', {
      method: 'POST',
      body: JSON.stringify({
        model_id: modelId,
        prompt,
        ...(options?.sessionId && { session_id: options.sessionId }),
        ...(options?.metaSessionId && { meta_session_id: options.metaSessionId }),
        ...(options?.turnId && { turn_id: options.turnId }),
      }),
    })
  },

  video: async (
    modelId: string,
    prompt: string,
    options?: ArenaPersistenceOptions
  ): Promise<ArenaVideoResponse> => {
    return request<ArenaVideoResponse>('/chat/arena/video', {
      method: 'POST',
      body: JSON.stringify({
        model_id: modelId,
        prompt,
        ...(options?.sessionId && { session_id: options.sessionId }),
        ...(options?.metaSessionId && { meta_session_id: options.metaSessionId }),
        ...(options?.turnId && { turn_id: options.turnId }),
      }),
    })
  },
}
